/**
 * `OD-37`, round 3 (R1): **the underwriting settings' key list, the one place that names them.**
 *
 * `docs/underwriter/underwriter-workflow.md` §15 lists the figures the run reads; `grille-de-notation-v2.md`
 * §6 says they are Hakili's defaults, that a bank may override each in its credit policy, and that no
 * prompt text carries a number. This file names them: the key, the type, the bounds, and Hakili's default.
 * The migration's `CHECK`s and seed, the Prisma models, the bank policy's door (`lending`) and the run's
 * resolver (`intelligence`) all derive from this list, and
 * `services/api/src/modules/intelligence/domain/underwriting-settings.agreement.spec.ts` asserts they agree.
 *
 * **Why it is in contracts.** Two modules read the list , `lending` validates a bank's overrides, `intelligence`
 * resolves them , and the module boundaries let neither import the other's domain (and an `api` file may not
 * import its own module's domain). A list both must obey is what contracts holds.
 *
 * **Empty is a value.** Two keys have no default because they are the bank's own credit-policy parameters
 * (`reste_a_vivre_min`, `total_debt_ratio_max`: the decision of 9 October, « pas de plancher par défaut », and workflow
 * 6.9; round 30, D-1013-11, left them empty on purpose; round 27 filled the three fonctionnaire retirement ages, D-1013-3:
 * 65, 61, 58; round 30 filled the staleness threshold of a declared commitment balance, 90 days). They resolve to `null`,
 * are listed in `missing`, and are never filled with a guess: a check that reads one says so in words (workflow 5.4,
 * « âge de retraite non paramétré ») and the instrument counts it.
 *
 * **Not here, on purpose.** The quotité cessible, the maximum term, the TAEG ceiling and the vehicle ages
 * live on `lending.credit_policies`; the price gap tolerance lives on the expertise grid
 * (`expertise.conformity_grid_versions.tolerance_pct`, default 10). A second copy of either would be a
 * second truth, so neither is a key here.
 *
 * **Pure.** `resolve` has no clock and no pool: the same inputs give the same answer, and it mutates
 * neither of them.
 */

export type SettingType = "integer" | "boolean";

export interface SettingSpec {
  readonly key: string;
  readonly type: SettingType;
  /** Hakili's default; `null` = empty until the founder provides it (integers only). */
  readonly hakiliDefault: number | boolean | null;
  /** Inclusive bounds of an integer, enforced here and by the singleton's `CHECK`. */
  readonly min?: number;
  readonly max?: number;
}

/** The thirty-eight keys (round 12: `retirement_age_prive` and `retirement_age_contractuel_etat` split into seven; round 16b: the PDF page limit), in the order of the rounds' orders (twenty-eight at round 3, the staleness at 6, the debit date at 9b-1, the name tolerance at 9b-2, the default quotité cessible at 11a). */
export const SETTING_SPECS = [
  { key: "payslip_recency_days", type: "integer", hakiliDefault: 60, min: 1, max: 365 },
  { key: "proforma_validity_days", type: "integer", hakiliDefault: 30, min: 1, max: 365 },
  { key: "hb_incident_penalty", type: "integer", hakiliDefault: 25, min: 0, max: 100 },
  { key: "hb_negative_month_penalty", type: "integer", hakiliDefault: 10, min: 0, max: 100 },
  { key: "hb_irregular_salary_penalty", type: "integer", hakiliDefault: 20, min: 0, max: 100 },
  { key: "hb_salary_date_tolerance_days", type: "integer", hakiliDefault: 15, min: 0, max: 31 },
  { key: "incidents_incertain_from", type: "integer", hakiliDefault: 1, min: 1, max: 100 },
  { key: "incidents_non_conforme_from", type: "integer", hakiliDefault: 2, min: 1, max: 100 },
  { key: "apport_full_score_pct", type: "integer", hakiliDefault: 20, min: 1, max: 100 },
  { key: "seniority_min_months", type: "integer", hakiliDefault: 12, min: 0, max: 600 },
  { key: "cdd_accepted", type: "boolean", hakiliDefault: true },
  { key: "cdd_term_may_exceed_contract", type: "boolean", hakiliDefault: false },
  { key: "retirement_term_cap", type: "boolean", hakiliDefault: true },
  { key: "retirement_age_fonctionnaire_a", type: "integer", hakiliDefault: 65, min: 18, max: 100 },
  { key: "retirement_age_fonctionnaire_b", type: "integer", hakiliDefault: 61, min: 18, max: 100 },
  { key: "retirement_age_fonctionnaire_c", type: "integer", hakiliDefault: 58, min: 18, max: 100 },
  // `OD-37` round 12 (D-1012-1): the founder's table, one key per row, the ratified ages as defaults; the three
  // fonctionnaire keys above stay empty (never defaulted) until the text of the Statut général is read.
  { key: "retirement_age_private_a", type: "integer", hakiliDefault: 60, min: 18, max: 100 },
  { key: "retirement_age_private_b_e", type: "integer", hakiliDefault: 58, min: 18, max: 100 },
  { key: "retirement_age_state_contract_a", type: "integer", hakiliDefault: 60, min: 18, max: 100 },
  { key: "retirement_age_state_contract_b_e", type: "integer", hakiliDefault: 58, min: 18, max: 100 },
  { key: "retirement_age_parapublic_a", type: "integer", hakiliDefault: 65, min: 18, max: 100 },
  { key: "retirement_age_parapublic_b", type: "integer", hakiliDefault: 62, min: 18, max: 100 },
  { key: "retirement_age_parapublic_c_e", type: "integer", hakiliDefault: 58, min: 18, max: 100 },
  { key: "nonrecurring_inclusion_pct", type: "integer", hakiliDefault: 0, min: 0, max: 100 },
  { key: "nonrecurring_signal_pct", type: "integer", hakiliDefault: 20, min: 0, max: 100 },
  { key: "recurring_line_tolerance_pct", type: "integer", hakiliDefault: 10, min: 0, max: 100 },
  { key: "reste_a_vivre_min", type: "integer", hakiliDefault: null, min: 0, max: 1_000_000_000 },
  { key: "total_debt_ratio_max", type: "integer", hakiliDefault: null, min: 1, max: 100 },
  { key: "grade_threshold_a_plus", type: "integer", hakiliDefault: 85, min: 0, max: 100 },
  { key: "grade_threshold_a", type: "integer", hakiliDefault: 75, min: 0, max: 100 },
  { key: "grade_threshold_b_plus", type: "integer", hakiliDefault: 65, min: 0, max: 100 },
  { key: "grade_threshold_b", type: "integer", hakiliDefault: 55, min: 0, max: 100 },
  { key: "salary_gap_tolerance_pct", type: "integer", hakiliDefault: 5, min: 0, max: 100 },
  // `OD-37`, round 6: workflow v3 Step 0 and §15 name it, empty until the founder provides the figure. Read ONCE,
  // at the freeze (`lending.SubmissionCreated`): a balance older than this many days at submission is flagged on
  // the snapshot, « solde déclaré ancien », and never refused.
  { key: "commitment_balance_staleness_days", type: "integer", hakiliDefault: 90, min: 1, max: 3650 },
  // `OD-37`, round 9b-1 (architect's ruling T4): « approximate date » of a recurring debit (workflow §3.5) is the same
  // calendar day within this many days, in each of the three months. The first key added after version 1 with a
  // default: the column is `NOT NULL DEFAULT 3`, so version 1 reads 3 without any published row being rewritten.
  { key: "recurring_debit_date_tolerance_days", type: "integer", hakiliDefault: 3, min: 0, max: 31 },
  // `OD-37`, round 9b-2 (architect's ruling V6): « a minor spelling difference » of a name (workflow §3.1) is a
  // difference of at most this many letters on the normalised full name. A bank may set 0: any difference is then
  // « noms divergents ». The second key with a default added after version 1: `NOT NULL DEFAULT 2`.
  { key: "name_spelling_tolerance_letters", type: "integer", hakiliDefault: 2, min: 0, max: 5 },
  // `OD-37`, round 11a (architect's ruling Z1): Hakili's default quotité cessible, whole percent of the net salary, the
  // share the console run (which has no bank and so no policy) reads Capacité and the quotité point on. A bank's own
  // quotité in its policy stays what its run reads, as before. The third key added after version 1 with a default:
  // `NOT NULL DEFAULT 40`.
  { key: "assignable_share_default_pct", type: "integer", hakiliDefault: 40, min: 0, max: 100 },
  // `OD-37`, round 16b (D-1013-1): the most pages of one PDF pièce a reader is sent. A PDF is rendered page by page into
  // images, in order, up to this many; the pages beyond are cut and the cut is a finding, « pièce tronquée à {n} pages »,
  // never silent. The fourth key added after version 1 with a default: `NOT NULL DEFAULT 12`.
  { key: "reader_pdf_pages_max", type: "integer", hakiliDefault: 12, min: 1, max: 50 },
] as const satisfies readonly SettingSpec[];

export type UnderwritingSettingKey = (typeof SETTING_SPECS)[number]["key"];

type SpecOf<K extends UnderwritingSettingKey> = Extract<(typeof SETTING_SPECS)[number], { readonly key: K }>;

/** A resolved value: `null` is allowed only on the keys whose default is empty. */
export type UnderwritingSettings = {
  readonly [K in UnderwritingSettingKey]: SpecOf<K> extends { readonly type: "boolean" }
    ? boolean
    : SpecOf<K> extends { readonly hakiliDefault: null }
      ? number | null
      : number;
};

export type SettingSource = "default" | "override";

export interface ResolvedSettings {
  /**
   * `OD-37` round 9b-2: the published version of Hakili's defaults these values were resolved from, so a run can say
   * which version it read; absent where the values were not read from a published row (a pure `resolve`, the
   * fall-back to the defaults).
   */
  readonly version?: number;
  readonly values: UnderwritingSettings;
  /** The empty-until-founder keys that still resolve to `null`. */
  readonly missing: readonly UnderwritingSettingKey[];
  readonly sources: Readonly<Record<UnderwritingSettingKey, SettingSource>>;
}

export const SETTING_KEYS: readonly UnderwritingSettingKey[] = SETTING_SPECS.map((spec) => spec.key);

const SPEC_BY_KEY: ReadonlyMap<string, SettingSpec> = new Map(SETTING_SPECS.map((spec) => [spec.key, spec]));

/** The empty-until-founder keys. */
export const EMPTY_KEYS: readonly UnderwritingSettingKey[] = SETTING_SPECS.filter(
  (spec) => spec.hakiliDefault === null,
).map((spec) => spec.key);

/** Hakili's defaults, exactly the seed of `intelligence.underwriting_settings` version 1. */
export const HAKILI_DEFAULTS: UnderwritingSettings = Object.fromEntries(
  SETTING_SPECS.map((spec) => [spec.key, spec.hakiliDefault]),
) as unknown as UnderwritingSettings;

/** Raised for a key or a value the list does not admit; names the key. */
export class UnderwritingSettingsError extends Error {
  constructor(
    readonly key: string,
    message: string,
  ) {
    super(message);
    this.name = "UnderwritingSettingsError";
  }
}

/** The value as the key's spec admits it, or a refusal naming the key. */
export function checkSettingValue(spec: SettingSpec, value: unknown): number | boolean {
  if (spec.type === "boolean") {
    if (typeof value !== "boolean") {
      throw new UnderwritingSettingsError(spec.key, `${spec.key} est un booléen (vrai ou faux).`);
    }
    return value;
  }
  const min = spec.min ?? 0;
  const max = spec.max ?? Number.MAX_SAFE_INTEGER;
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    throw new UnderwritingSettingsError(
      spec.key,
      `${spec.key} est un entier de ${String(min)} à ${String(max)}.`,
    );
  }
  return value;
}

export function settingSpecOf(key: string): SettingSpec {
  const spec = SPEC_BY_KEY.get(key);
  if (spec === undefined) {
    throw new UnderwritingSettingsError(key, `${key} n'est pas un réglage de la souscription.`);
  }
  return spec;
}

/**
 * A bank's overrides as the policy door receives them: every key known, every value of its key's type.
 * Needs no published defaults, so the door (in `lending`) validates without reading `intelligence`'s tables.
 * Returns the map unchanged in content, typed.
 */
export function validateOverrides(
  overrides: Readonly<Record<string, unknown>>,
): Partial<Record<UnderwritingSettingKey, number | boolean>> {
  const out: Partial<Record<UnderwritingSettingKey, number | boolean>> = {};
  for (const [key, value] of Object.entries(overrides)) {
    const spec = settingSpecOf(key);
    out[spec.key as UnderwritingSettingKey] = checkSettingValue(spec, value);
  }
  return out;
}
